<template>
  <section class="testimonials-section">
    <h2 class="testimonials-heading">
      <span>{{ t('testimonials.heading_part1') }}</span>
      <span class="highlight">{{ t('testimonials.heading_highlight') }}</span>
      <span>{{ t('testimonials.heading_part2') }}</span>
    </h2>

    <div class="testimonials-grid">
      <article
        v-for="item in items"
        :key="item.id"
        class="testimonial-card"
      >
        <div class="quote-badge" aria-hidden="true">
          <svg class="quote-mark" width="22" height="22" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg">
            <circle cx="14" cy="14" r="14" fill="#1a1a1a"/>
            <path d="M14 24 Q14 36 0 52 L8 58 Q24 40 28 20 Z" fill="#1a1a1a"/>
            <circle cx="44" cy="14" r="14" fill="#1a1a1a"/>
            <path d="M44 24 Q44 36 30 52 L38 58 Q54 40 58 20 Z" fill="#1a1a1a"/>
          </svg>
        </div>

        <p class="testimonial-text" v-html="item.text"></p>

        <div class="testimonial-meta">
          <h3 class="testimonial-name">{{ item.name }}</h3>
          <p class="testimonial-level">{{ item.level }}</p>
          <div class="testimonial-stars" :aria-label="`${item.rating} out of 5`">
            <span v-for="n in item.rating" :key="n" aria-hidden="true">★</span>
          </div>
        </div>
      </article>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLanguage } from '../../composables/useLanguage'

interface TestimonialItem {
  id: number
  name: string
  level: string
  text: string
  rating: number
}

const { t } = useLanguage()
const { tm, rt } = useI18n()

const items = computed<TestimonialItem[]>(() => {
  const raw = tm('testimonials.items') as Array<Record<string, unknown>>
  return raw.map((item) => ({
    id: Number(item.id),
    name: rt(item.name as string),
    level: rt(item.level as string),
    text: rt(item.text as string),
    rating: Number(item.rating),
  }))
})
</script>

<style scoped>
.testimonials-section {
  width: 100%;
  padding: clamp(40px, 6vw, 80px) clamp(20px, 4vw, 60px);
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(30px, 5vw, 60px);
}

html[lang="ar"] .testimonials-section {
  direction: rtl;
}

.testimonials-heading {
  font-size: clamp(22px, 3vw, 36px);
  font-weight: 700;
  line-height: 1.3;
  margin: 0;
  color: #1a1a1a;
  text-align: center;
  max-width: 1100px;
}

.testimonials-heading .highlight {
  color: #ff8f37;
}

.testimonials-grid {
  width: 100%;
  max-width: 1300px;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: clamp(12px, 1.2vw, 18px);
  align-items: stretch;
}

.testimonial-card {
  position: relative;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  padding: clamp(28px, 2.5vw, 38px) clamp(16px, 1.5vw, 22px) clamp(20px, 2vw, 28px);
  display: flex;
  flex-direction: column;
  gap: clamp(16px, 1.5vw, 22px);
  text-align: start;
}

html[lang="ar"] .testimonial-card {
  direction: rtl;
}

.quote-badge {
  position: absolute;
  top: -20px;
  right: 50%;
  transform: translateX(50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #ff8f37;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(255, 143, 55, 0.35);
}

.quote-mark {
  display: block;
  transform: scaleY(-1);
}

.testimonial-text {
  flex: 1;
  font-size: clamp(12px, 0.95vw, 14px);
  line-height: 1.75;
  color: #4a4a4a;
  margin: 0;
  font-weight: 400;
}

.testimonial-text :deep(strong) {
  font-weight: 700;
  color: #1a1a1a;
}

.testimonial-meta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(4px, 0.5vw, 6px);
  margin-top: auto;
  text-align: center;
}

.testimonial-name {
  font-size: clamp(15px, 1.2vw, 18px);
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
}

.testimonial-level {
  font-size: clamp(11px, 0.8vw, 12px);
  color: #8a8a8a;
  margin: 0;
  font-weight: 400;
}

.testimonial-stars {
  display: flex;
  gap: 3px;
  color: #ff8f37;
  font-size: clamp(13px, 1vw, 15px);
  margin-top: clamp(2px, 0.5vw, 6px);
  letter-spacing: 1px;
}

@media (max-width: 1200px) {
  .testimonials-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  .testimonial-card:nth-child(n + 4) {
    display: none;
  }
}

@media (max-width: 768px) {
  .testimonials-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 28px 16px;
  }
  .testimonial-card:nth-child(n + 4) {
    display: flex;
  }
  .testimonial-card:nth-child(n + 5) {
    display: none;
  }
  .testimonial-text {
    font-size: 13px;
  }
}

@media (max-width: 520px) {
  .testimonials-grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }
  .testimonial-card:nth-child(n + 5) {
    display: flex;
  }
}
</style>
